import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import { TileSubheader } from 'Components/DashboardTile/TileContentMaker/components/common/commonComponents';
import { fm } from 'Components/FormatMessage';
import PlatformConditional from 'Components/PlatformConditional';
import { isPartialDate } from 'neuro-utils';
import { useState, useEffect, Fragment } from 'react';
import { useIntl } from 'react-intl';
import { patientHasRespiratoryFailureDiagnosis, patientHasSleepApneaDiagnosis } from 'Routes/Treatment/utils';
import { useAppSelector } from 'Store/index';
import browserLanguage from 'Utility/browserLanguage';
import { formatPartialDate } from 'Utility/date';
import { filterByDocumentType } from 'Utility/documentHandling';
import { getICD10Description } from '../../../utility/randomUtil';
import { sortAndTakeMany } from '../utils';

import type { TRelevantDocumentType } from '../utils';

const ComorbData = ({ doc }: { doc: IComorbidity }) => {
  const locale = useAppSelector((state: IState) => state.settings.userSettings.uiLanguage) ?? browserLanguage;

  const [icd10description, setIcd10description] = useState<string | undefined>(undefined);

  useEffect(() => {
    let isMounted = true;
    if (doc.classification?.icd10code) {
      getICD10Description(doc.classification.icd10code, locale)
        .then(d => {
          if (isMounted) {
            setIcd10description(d);
          }
        })
        .catch(() => {
          return;
        });
    }
    return (): void => {
      isMounted = false;
    };
  }, [doc.classification?.icd10code]);

  return (
    <div
      style={{
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '29rem',
      }}
    >
      <span style={{ fontWeight: 600 }}>{doc.classification?.icd10code}</span>
      {icd10description ? <span style={{ fontWeight: 400, fontSize: '1.3rem' }}>{` - ${icd10description}`}</span> : ''}
    </div>
  );
};

const Hospitalization = ({ doc }: { doc: IHospitalization }) => {
  const locale = useAppSelector((state: IState) => state.settings.userSettings.uiLanguage) ?? browserLanguage;

  const [icd10description, setIcd10description] = useState<string | undefined>(undefined);

  useEffect(() => {
    let isMounted = true;
    if (doc.classification?.icd10code) {
      getICD10Description(doc.classification.icd10code, locale)
        .then(d => {
          if (isMounted) {
            setIcd10description(d);
          }
        })
        .catch(() => {
          return;
        });
    }
    return (): void => {
      isMounted = false;
    };
  }, [doc.classification]);

  return (doc.hospitalizationType === 'planned' || doc.hospitalizationType === 'acute') && doc.classification ? (
    <div
      style={{
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '18rem',
      }}
    >
      <span style={{ fontWeight: 600 }}>{doc.classification.icd10code}</span>
      {icd10description ? (
        <span style={{ fontWeight: 400, fontSize: '1.3rem', overflow: 'hidden' }}>{` - ${icd10description}`}</span>
      ) : (
        ''
      )}
    </div>
  ) : (
    <></>
  );
};

const filterEndedComorbidities = (doc: IDocumentWithTypeV2<'comorbidity'>) => !isPartialDate(doc.endDate);
const filterEndedPregnancies = (doc: IDocumentWithTypeV2<'pregnancy'>) => !isPartialDate(doc.endDate);
const filterEndedContraceptions = (doc: IDocumentWithTypeV2<'contraception'>) => !isPartialDate(doc.endDate);

const ComorbidityDash = ({ documents }: IOwnProps) => {
  const diagnosis = filterByDocumentType(documents, 'diagnosis');
  const respiratoryFailureDiagnosis = patientHasRespiratoryFailureDiagnosis(diagnosis);
  const sleepApneaDiagnosis = patientHasSleepApneaDiagnosis(diagnosis);

  const latestSickLeave = sortAndTakeMany('sickLeave', documents).at(0);

  const allComorbs = sortAndTakeMany('comorbidity', documents);

  // SMA
  const comorbiditiesSMA = sortAndTakeMany('comorbidity', documents, 2, filterEndedComorbidities);
  const comorbiditiesSMALength = sortAndTakeMany('comorbidity', documents, undefined, filterEndedComorbidities).length;

  // Epilepsy
  const comorbiditiesEpilepsy = sortAndTakeMany('comorbidity', documents, 1, filterEndedComorbidities);
  const comorbiditiesEpilepsyLength = sortAndTakeMany(
    'comorbidity',
    documents,
    undefined,
    filterEndedComorbidities,
  ).length;

  const latestOngoingPregnancy = sortAndTakeMany('pregnancy', documents, undefined, filterEndedPregnancies).at(0);

  const latestOngoingContraception = sortAndTakeMany(
    'contraception',
    documents,
    undefined,
    filterEndedContraceptions,
  ).at(0);

  // MS
  const comorbiditiesMS = sortAndTakeMany('comorbidity', documents, undefined, filterEndedComorbidities);

  // NINMT
  const comorbiditiesNINMT = sortAndTakeMany('comorbidity', documents, undefined, filterEndedComorbidities);
  const comorbiditiesTreatedWithNinmt = comorbiditiesNINMT.filter(comorbity => comorbity.ninmtSymptom);
  // If there are more than diagnoses treated with ninmt, cap shown to 3
  const comorbiditiesTreatedWithNinmtMaxShown = comorbiditiesTreatedWithNinmt.slice(0, 3);
  const comorbiditiesTreatedWithoutNinmt = comorbiditiesNINMT.filter(comorbity => !comorbity.ninmtSymptom);

  // S&R
  const comorbiditiesRespFailure = allComorbs
    .filter(c => c.sicknessEffectOnRespiratoryFailure === 'respiratoryFailureReason')
    .slice(0, 3);

  const comorbiditiesSleepApnea = sortAndTakeMany('comorbidity', documents, 3, filterEndedComorbidities);
  const comorbiditiesSleepApneaLength = sortAndTakeMany(
    'comorbidity',
    documents,
    undefined,
    filterEndedComorbidities,
  ).length;

  // Mgravis
  const hospitalWardDocs = sortAndTakeMany('hospitalization', documents).filter(
    h => h.classification?.icd10code === 'G70' && h.hospitalizationType === 'acute' && h.department === 'hospitalWard',
  );
  const intensiveCareDocs = sortAndTakeMany('hospitalization', documents).filter(
    h => h.classification?.icd10code === 'G70' && h.hospitalizationType === 'acute' && h.department === 'intensiveCare',
  );

  const { formatMessage } = useIntl();

  return (
    <Fragment>
      <PlatformConditional platform="ms">
        <TileContentMaker
          type="recentList"
          data={comorbiditiesMS.map(c => ({
            date: isPartialDate(c.date) ? formatPartialDate(c.date) : '',
            title: <ComorbData doc={c} />,
            key: c._id,
          }))}
        />
      </PlatformConditional>
      <PlatformConditional platform={['sma', 'dmd']}>
        <TileContentMaker
          type="listWithSubheaders"
          data={{
            [formatMessage({ id: 'comorbidity.comorbidities' })]: {
              type: 'recentList',
              documentsLength: comorbiditiesSMALength > 0 ? comorbiditiesSMALength : null,
              data: comorbiditiesSMA.map(c => ({
                date: isPartialDate(c.date) ? formatPartialDate(c.date) : '',
                title: <ComorbData doc={c} />,
                key: c._id,
              })),
            },
            [formatMessage({ id: 'comorbidity.latestHospitalization' })]: {
              type: 'specificList',
              data: sortAndTakeMany('hospitalization', documents, 1).map(h => ({
                date: isPartialDate(h.date) ? formatPartialDate(h.date) : '',
                title: <Hospitalization doc={h} />,
                endDate: isPartialDate(h.endDate) ? formatPartialDate(h.endDate) : null,
                key: h._id,
              })),
            },
          }}
        />
      </PlatformConditional>
      <PlatformConditional platform="epilepsy">
        <>
          <TileContentMaker
            type="listWithSubheaders"
            data={{
              [formatMessage({ id: 'comorbidity.comorbidities' })]: {
                type: 'recentList',
                documentsLength: comorbiditiesEpilepsyLength > 0 ? comorbiditiesEpilepsyLength : null,
                data: comorbiditiesEpilepsy.map(c => ({
                  date: isPartialDate(c.date) ? formatPartialDate(c.date) : '',
                  title: <ComorbData doc={c} />,
                  key: c._id,
                })),
              },
              ...(latestOngoingPregnancy && {
                [formatMessage({ id: 'comorbidity.activePregnancy' })]: {
                  type: 'specificList',
                  data: [
                    {
                      date: isPartialDate(latestOngoingPregnancy.date)
                        ? formatPartialDate(latestOngoingPregnancy.date)
                        : '',
                      title: formatMessage({ id: 'comorbidity.pregnancy' }),
                      key: 'pregnancy',
                    },
                  ],
                },
              }),
            }}
          />
          {latestOngoingContraception && (
            <>
              <TileSubheader title={formatMessage({ id: 'comorbidity.latestMethodOfContraception' })} />
              <TileContentMaker
                type="specificListNoDate"
                data={[
                  {
                    title: formatMessage({ id: 'comorbidity.methodOfContraception' }),
                    value: latestOngoingContraception.contraceptiveClassification && (
                      <Fragment>
                        <span>
                          {fm(
                            `comorbidity.opts.contraceptiveClassification.${latestOngoingContraception.contraceptiveClassification}`,
                          )}
                        </span>
                        {latestOngoingContraception.contraceptive && (
                          <span>
                            : {fm(`comorbidity.opts.contraceptive.${latestOngoingContraception.contraceptive}`)}
                          </span>
                        )}
                      </Fragment>
                    ),
                    key: 'methodOfContraception',
                  },
                ]}
              />
            </>
          )}
        </>
      </PlatformConditional>
      <PlatformConditional platform="sleepApnea">
        <TileContentMaker
          type="listWithSubheaders"
          data={{
            ...(respiratoryFailureDiagnosis && comorbiditiesRespFailure.length
              ? {
                  [formatMessage({ id: 'comorbidity.comorbiditiesThatCauseRespiratoryFailure' })]: {
                    type: 'recentList',
                    documentsLength: comorbiditiesRespFailure.length > 0 ? comorbiditiesRespFailure.length : null,
                    data: comorbiditiesRespFailure.map(c => ({
                      date: isPartialDate(c.date) ? formatPartialDate(c.date) : '',
                      title: <ComorbData doc={c} />,
                      key: c._id,
                    })),
                  },
                }
              : {}),
            ...(sleepApneaDiagnosis && !respiratoryFailureDiagnosis && comorbiditiesSleepApnea.length
              ? {
                  [formatMessage({ id: 'comorbidity.comorbidities' })]: {
                    type: 'recentList',
                    documentsLength: comorbiditiesSleepApneaLength > 0 ? comorbiditiesSleepApneaLength : null,
                    data: comorbiditiesSleepApnea.map(c => ({
                      date: isPartialDate(c.date) ? formatPartialDate(c.date) : '',
                      title: <ComorbData doc={c} />,
                      key: c._id,
                    })),
                  },
                }
              : {}),
            ...(latestSickLeave
              ? {
                  [formatMessage({ id: 'comorbidity.latestSickLeave' })]: {
                    type: 'specificList',
                    data: [
                      {
                        date: isPartialDate(latestSickLeave.date) ? formatPartialDate(latestSickLeave.date) : '',
                        endDate: isPartialDate(latestSickLeave.endDate)
                          ? formatPartialDate(latestSickLeave.endDate)
                          : null,
                        title: fm(`comorbidity.opts.reasonForSickLeave.${latestSickLeave.reasonForSickLeave}`),
                        key: latestSickLeave._id,
                      },
                    ],
                  },
                }
              : {}),
          }}
        />
      </PlatformConditional>
      <PlatformConditional platform={'ninmt'}>
        <TileContentMaker
          type="listWithSubheaders"
          data={{
            [formatMessage({ id: 'comorbidity.diagnosesTreatedWithNinmt' })]: {
              type: 'recentList',
              documentsLength: comorbiditiesTreatedWithNinmt.length > 0 ? comorbiditiesTreatedWithNinmt.length : null,
              data:
                comorbiditiesTreatedWithNinmt.length > 3 && comorbiditiesTreatedWithoutNinmt.length > 0
                  ? comorbiditiesTreatedWithNinmtMaxShown.map(c => ({
                      date: isPartialDate(c.date) ? formatPartialDate(c.date) : '',
                      title: <ComorbData doc={c} />,
                      key: c._id,
                    }))
                  : comorbiditiesTreatedWithNinmt.map(c => ({
                      date: isPartialDate(c.date) ? formatPartialDate(c.date) : '',
                      title: <ComorbData doc={c} />,
                      key: c._id,
                    })),
            },
            [formatMessage({ id: 'comorbidity.otherDiagnoses' })]: {
              type: 'recentList',
              documentsLength:
                comorbiditiesTreatedWithoutNinmt.length > 0 ? comorbiditiesTreatedWithoutNinmt.length : null,
              data: comorbiditiesTreatedWithoutNinmt.map(c => ({
                date: isPartialDate(c.date) ? formatPartialDate(c.date) : '',
                title: <ComorbData doc={c} />,
                key: c._id,
              })),
            },
          }}
        />
      </PlatformConditional>
      <PlatformConditional platform={'mgravis'}>
        <TileContentMaker
          type="specificListMultipleRows"
          itemsCanFitAmount={4}
          data={[
            ...(hospitalWardDocs.length > 0
              ? [
                  {
                    date: isPartialDate(hospitalWardDocs.at(0)?.date)
                      ? formatPartialDate(hospitalWardDocs.at(0)?.date)
                      : '',
                    endDate: isPartialDate(hospitalWardDocs.at(0)?.endDate)
                      ? formatPartialDate(hospitalWardDocs.at(0)?.endDate)
                      : '',
                    title: fm('comorbidity.mgravis.latestHospitalWard'),
                    key: 'latestHospitalWard',
                  },
                  {
                    title: fm('comorbidity.mgravis.totalHospitalWards'),
                    value: hospitalWardDocs.length,
                    key: 'hospitalWardTotal',
                  },
                ]
              : []),
            ...(intensiveCareDocs.length > 0
              ? [
                  {
                    date: isPartialDate(intensiveCareDocs.at(0)?.date)
                      ? formatPartialDate(intensiveCareDocs.at(0)?.date)
                      : '',
                    endDate: isPartialDate(intensiveCareDocs.at(0)?.endDate)
                      ? formatPartialDate(intensiveCareDocs.at(0)?.endDate)
                      : '',
                    title: fm('comorbidity.mgravis.latestIntensiveCare'),
                    key: 'latestIntensiveCare',
                  },
                  {
                    title: fm('comorbidity.mgravis.totalIntensiveCares'),
                    value: intensiveCareDocs.length,
                    key: 'intensiveCareTotal',
                  },
                ]
              : []),
          ]}
        />
      </PlatformConditional>
    </Fragment>
  );
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>;
}

export default ComorbidityDash;
